import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Plus, Play, Square, AlertCircle, Save, Info } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '../../lib/utils'
import { StepBlock } from './StepBlock'
import { StartBlock } from './StartBlock'
import { StopBlock } from './StopBlock'
import type { WorkflowStep } from '../../types/workflow'

interface WorkflowCanvasProps {
  steps: WorkflowStep[]
  selectedStepId: string | null
  onStepSelect: (stepId: string | null) => void
  onStepUpdate: (stepId: string, updates: Partial<WorkflowStep>) => void
  onStepDelete: (stepId: string) => void
  onStepReorder: (oldIndex: number, newIndex: number) => void
  onRunWorkflow?: () => void
  onStopWorkflow?: () => void
  onStartSession?: () => void
  onSaveWorkflow?: () => void
  className?: string
  compact?: boolean
  execution?: {
    status: 'idle' | 'running' | 'paused' | 'completed' | 'failed' | 'stopped'
    currentStepIndex: number | null
    stepResults: Record<string, 'pending' | 'running' | 'success' | 'failed'>
    startedAt: string | null
    currentTaskId: string | null
    taskIds: string[]
    shouldStop: boolean
  }
  browserSession?: {
    session: any | null
    status: 'disconnected' | 'connecting' | 'connected' | 'error'
  }
  sessionHealth?: {
    isHealthy: boolean
    isValidating: boolean
    lastCheck: Date | null
    error: string | null
    canNavigate: boolean
    canExecuteTasks: boolean
  }
}

export function WorkflowCanvas({
  steps,
  selectedStepId,
  onStepSelect,
  onStepUpdate,
  onStepDelete,
  onStepReorder: _onStepReorder,
  onRunWorkflow,
  onStopWorkflow,
  onStartSession,
  onSaveWorkflow,
  className,
  compact = false,
  execution,
  browserSession,
  sessionHealth
}: WorkflowCanvasProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: 'workflow-canvas',
    data: {
      type: 'canvas'
    }
  })

  const enabledSteps = steps.filter(step => step.enabled)
  const disabledSteps = steps.filter(step => !step.enabled)
  const totalSteps = steps.length

  const handleStepToggle = (stepId: string, enabled: boolean) => {
    onStepUpdate(stepId, { enabled })
  }

  const handleStepEdit = (stepId: string) => {
    onStepSelect(stepId)
  }

  const handleStepDuplicate = (step: WorkflowStep) => {
    const duplicatedStep: WorkflowStep = {
      ...step,
      id: `temp-${Date.now()}`,
      step_index: steps.length,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    
    // This would need to be handled by parent component
    console.log('Duplicate step:', duplicatedStep)
  }

  // Compact mode for sidebar
  if (compact) {
    return (
      <div className={cn("compact-workflow-canvas", className)}>
        {/* Compact Header */}
        <div className="p-3 border-b bg-muted/50">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold">Workflow Steps</h4>
            <Badge variant="outline" className="text-xs">
              {totalSteps}
            </Badge>
          </div>
          {enabledSteps.length !== totalSteps && (
            <p className="text-xs text-muted-foreground">
              {enabledSteps.length} of {totalSteps} enabled
            </p>
          )}
        </div>

        {/* Compact Steps List */}
        <div
          ref={setNodeRef}
          className={cn(
            "flex-1 p-2 space-y-2 min-h-[200px] border-2 border-dashed transition-colors",
            isOver ? "border-primary bg-primary/5" : "border-muted-foreground/25"
          )}
        >
          {steps.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-xs">No steps yet</p>
              <p className="text-xs">Drag from palette to add</p>
            </div>
          ) : (
            <SortableContext items={steps.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {steps.map((step) => (
                <div key={step.id} className="mb-2">
                  {step.id === 'start-block' ? (
                    <StartBlock
                      step={step}
                      isSelected={selectedStepId === step.id}
                      onSelect={() => handleStepEdit(step.id)}
                      onToggle={(enabled) => handleStepToggle(step.id, enabled)}
                      onDelete={() => onStepDelete(step.id)}
                      onDuplicate={() => handleStepDuplicate(step)}
                      onUpdate={(updates) => onStepUpdate(step.id, updates)}
                      className="compact-step"
                    />
                  ) : step.id === 'end-block' ? (
                    <StopBlock
                      step={step}
                      isSelected={selectedStepId === step.id}
                      onSelect={() => handleStepEdit(step.id)}
                      onToggle={(enabled) => handleStepToggle(step.id, enabled)}
                      onDelete={() => onStepDelete(step.id)}
                      onDuplicate={() => handleStepDuplicate(step)}
                      className="compact-step"
                    />
                  ) : (
                    <StepBlock
                      step={step}
                      isSelected={selectedStepId === step.id}
                      onSelect={() => handleStepEdit(step.id)}
                      onToggle={(enabled) => handleStepToggle(step.id, enabled)}
                      onDelete={() => onStepDelete(step.id)}
                      onDuplicate={() => handleStepDuplicate(step)}
                      onUpdate={(updates) => onStepUpdate(step.id, updates)}
                      className="compact-step"
                    />
                  )}
                </div>
              ))}
            </SortableContext>
          )}
        </div>
      </div>
    )
  }

  // Regular mode
  return (
    <Card className={cn("h-full flex flex-col", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Workflow Canvas</CardTitle>
            <CardDescription>
              Drag steps here to build your workflow
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {totalSteps} step{totalSteps !== 1 ? 's' : ''}
            </Badge>
            {enabledSteps.length !== totalSteps && (
              <Badge variant="secondary">
                {enabledSteps.length} enabled
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Status Banner */}
      <div className="mx-6 mb-4">
        {/* Browser Session Status */}
        {browserSession?.status !== 'connected' ? (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-2">
            <div className="flex items-center gap-2 text-blue-700">
              <Info className="h-4 w-4" />
              <span className="text-sm font-medium">
                {browserSession?.status === 'connecting' ? 'Starting browser session...' :
                 browserSession?.status === 'error' ? 'Browser session error - please refresh' :
                 'Click "Start Session" to begin browser automation'}
              </span>
            </div>
          </div>
        ) : (
          /* Workflow Ready Status */
          enabledSteps.length > 0 && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-green-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-sm font-medium">
                    Ready to execute {enabledSteps.length} step{enabledSteps.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {sessionHealth && (
                  <div className="flex items-center gap-3 text-xs">
                    <div className="flex items-center gap-1">
                      <span className="text-green-600">Navigation</span>
                      <div className={`w-1.5 h-1.5 rounded-full ${sessionHealth.canNavigate ? 'bg-green-500' : 'bg-red-500'}`} />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-green-600">Tasks</span>
                      <div className={`w-1.5 h-1.5 rounded-full ${sessionHealth.canExecuteTasks ? 'bg-green-500' : 'bg-red-500'}`} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        )}

        {/* Execution Status */}
        {execution?.status === 'running' && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mt-2">
            <div className="flex items-center gap-2 text-blue-700">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium">
                Executing step {(execution.currentStepIndex || 0) + 1} of {enabledSteps.length}
              </span>
              {execution.currentTaskId && (
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                  Task: {execution.currentTaskId}
                </span>
              )}
            </div>
          </div>
        )}

        {execution?.status === 'completed' && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg mt-2">
            <div className="flex items-center gap-2 text-green-700">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-sm font-medium">Workflow completed successfully</span>
            </div>
          </div>
        )}

        {execution?.status === 'failed' && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg mt-2">
            <div className="flex items-center gap-2 text-red-700">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-sm font-medium">Workflow failed</span>
            </div>
          </div>
        )}

        {sessionHealth?.isValidating && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mt-2">
            <div className="flex items-center gap-2 text-blue-700">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium">Validating session health...</span>
            </div>
          </div>
        )}

        {sessionHealth && !sessionHealth.isHealthy && browserSession?.status === 'connected' && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg mt-2">
            <div className="flex items-center gap-2 text-red-700">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-sm font-medium">
                Session unhealthy: {sessionHealth.error || 'Unknown issue'}
              </span>
            </div>
          </div>
        )}
      </div>

      <CardContent className="flex-1 overflow-y-auto max-h-[calc(100vh-12rem)]">
        <div
          ref={setNodeRef}
          className={cn(
            "min-h-full p-4 rounded-lg border-2 border-dashed transition-colors",
            isOver ? "border-primary bg-primary/5" : "border-muted-foreground/25",
            steps.length === 0 && "flex items-center justify-center"
          )}
        >
          {steps.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <Plus className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">No steps yet</h3>
              <p className="text-muted-foreground mb-4">
                Drag blocks from the palette to start building your workflow
              </p>
              <div className="text-sm text-muted-foreground">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded bg-blue-500"></div>
                  <span>Mouse Actions</span>
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded bg-green-500"></div>
                  <span>Text & Input</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <div className="w-3 h-3 rounded bg-purple-500"></div>
                  <span>Navigation</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Enabled Steps */}
              <SortableContext 
                items={enabledSteps.map(step => step.id)} 
                strategy={verticalListSortingStrategy}
              >
                {enabledSteps.map((step, index) => {
                  const stepStatus = execution?.stepResults[step.id] || 'pending'
                  const isCurrentStep = execution?.currentStepIndex === index
                  const isExecuting = execution?.status === 'running'

                  return (
                  <div key={step.id} className="relative">
                    {/* Step Number with Execution Status */}
                    <div className={cn(
                      "absolute -left-8 top-1/2 transform -translate-y-1/2 w-6 h-6 rounded-full text-xs flex items-center justify-center font-medium transition-all duration-300",
                      stepStatus === 'success' && "bg-green-500 text-white",
                      stepStatus === 'failed' && "bg-red-500 text-white",
                      stepStatus === 'running' && "bg-blue-500 text-white animate-pulse",
                      stepStatus === 'pending' && !isCurrentStep && "bg-gray-300 text-gray-600",
                      stepStatus === 'pending' && isCurrentStep && isExecuting && "bg-yellow-500 text-white",
                      stepStatus === 'pending' && !isExecuting && "bg-primary text-primary-foreground"
                    )}>
                      {stepStatus === 'running' ? (
                        <div className="w-2 h-2 bg-white rounded-full animate-ping" />
                      ) : stepStatus === 'success' ? (
                        '✓'
                      ) : stepStatus === 'failed' ? (
                        '✗'
                      ) : (
                        index + 1
                      )}
                    </div>
                    
                    {step.id === 'start-block' ? (
                      <StartBlock
                        step={step}
                        isSelected={selectedStepId === step.id}
                        onSelect={() => handleStepEdit(step.id)}
                        onToggle={(enabled) => handleStepToggle(step.id, enabled)}
                        onDelete={() => onStepDelete(step.id)}
                        onDuplicate={() => handleStepDuplicate(step)}
                        onUpdate={(updates) => onStepUpdate(step.id, updates)}
                      />
                    ) : step.id === 'end-block' ? (
                      <StopBlock
                        step={step}
                        isSelected={selectedStepId === step.id}
                        onSelect={() => handleStepEdit(step.id)}
                        onToggle={(enabled) => handleStepToggle(step.id, enabled)}
                        onDelete={() => onStepDelete(step.id)}
                        onDuplicate={() => handleStepDuplicate(step)}
                      />
                    ) : (
                      <StepBlock
                        step={step}
                        isSelected={selectedStepId === step.id}
                        onSelect={() => handleStepEdit(step.id)}
                        onToggle={(enabled) => handleStepToggle(step.id, enabled)}
                        onDelete={() => onStepDelete(step.id)}
                        onDuplicate={() => handleStepDuplicate(step)}
                        onUpdate={(updates) => onStepUpdate(step.id, updates)}
                      />
                    )}

                    {/* Connection Line */}
                    {index < enabledSteps.length - 1 && (
                      <div className="flex justify-center py-2">
                        <div className="w-px h-4 bg-border"></div>
                      </div>
                    )}
                  </div>
                  )
                })}
              </SortableContext>

              {/* Disabled Steps Section */}
              {disabledSteps.length > 0 && (
                <div className="mt-8 pt-6 border-t">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <h4 className="font-medium text-muted-foreground">
                      Disabled Steps ({disabledSteps.length})
                    </h4>
                  </div>
                  
                  <div className="space-y-3 opacity-60">
                    {disabledSteps.map((step) => (
                      <StepBlock
                        key={step.id}
                        step={step}
                        isSelected={selectedStepId === step.id}
                        onSelect={() => handleStepEdit(step.id)}
                        onToggle={(enabled) => handleStepToggle(step.id, enabled)}
                        onDelete={() => onStepDelete(step.id)}
                        onDuplicate={() => handleStepDuplicate(step)}
                        onUpdate={(updates) => onStepUpdate(step.id, updates)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Execution Controls */}
              {enabledSteps.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-foreground">
                      Workflow Actions
                    </div>
                    <div className="flex gap-2">
                      {(browserSession?.status === 'disconnected' || browserSession?.status === 'connecting') && (
                        <Button
                          size="sm"
                          className="gap-2"
                          onClick={onStartSession}
                          disabled={!onStartSession || browserSession?.status === 'connecting'}
                          variant="outline"
                        >
                          {browserSession?.status === 'connecting' ? (
                            <>
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              Starting...
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4" />
                              Start Session
                            </>
                          )}
                        </Button>
                      )}
                      {onSaveWorkflow && (
                        <Button
                          size="sm"
                          className="gap-2"
                          onClick={onSaveWorkflow}
                          variant="default"
                        >
                          <Save className="h-4 w-4" />
                          Save Workflow
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="gap-2"
                        onClick={onRunWorkflow}
                        disabled={
                          !onRunWorkflow ||
                          execution?.status === 'running' ||
                          browserSession?.status !== 'connected' ||
                          (sessionHealth && !sessionHealth.isHealthy && !sessionHealth.isValidating)
                        }
                        variant={execution?.status === 'running' ? 'secondary' : 'default'}
                      >
                      {execution?.status === 'running' ? (
                        <>
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4" />
                          Run Workflow
                        </>
                      )}
                    </Button>

                    {/* Stop Workflow Button */}
                    {execution?.status === 'running' && (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="gap-2"
                        onClick={onStopWorkflow}
                        disabled={!onStopWorkflow}
                      >
                        <Square className="h-4 w-4" />
                        Stop
                      </Button>
                    )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
